import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Dialog,
  DialogContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@material-ui/core";
import { Close as CloseIcon } from "@mui/icons-material";
import { ErrorDetailList } from "../../templates";

const useStyles = makeStyles((theme) => ({
  customDialog: {
    "& .MuiDialog-paperWidthSm": {
      // Always allow up to 50rem, but never wider than the screen
      maxWidth: "50rem",
      width: "100%", // baseline: take full available width
      margin: "1rem", // 1rem (=16px) gutter on all sides

      // On small-and-up screens, let width shrink to content (up to maxWidth)
      [theme.breakpoints.up("sm")]: {
        width: "auto",
        margin: "auto",
      },

      // Same background & padding
      backgroundColor: "#f0f0f0",
      padding: "1rem",
    },
  },

  title: {
    fontSize: "1rem",
    fontWeight: "bold",
    marginBottom: "0.5rem",
    color: "#212121",
  },

  dialogContent: {
    padding: "1rem",
    backgroundColor: "#fff",
    fontSize: "1.125rem",
    minHeight: "12.5rem",
  },

  // Let the table fill the dialog’s inner width
  table: {
    width: "100%",
    tableLayout: "fixed",
  },

  // In narrow mode these will wrap or ellipsize; you can
  // reduce these rems if you need them slimmer on phone
  keyCell: {
    width: "12rem",
    fontSize: "1rem",
    color: "#616161",
  },
  valueCell: {
    width: "20rem", // 320px
    fontSize: "1rem",
    color: "#212121",
  },

  closeButton: {
    position: "absolute",
    top: "0.25rem",
    right: "0.25rem",
    color: theme.palette.grey[500],
    padding: "0.25rem",
  },
}));

const TRANSACTION = {
  GATEWAY: {
    234: "Alipay Scan",
    305: "WeChatPay CN Scan",
    320: "Boost Scan",
    329: "MCashMS",
    336: "TNG Scan",
    338: "UnionPay Scan",
    347: "GrabPay QR",
    354: "MaybankQR Scan",
    379: "GrabPay Scan",
    164: "PrestoPay Scan",
    19: "ShopeePay Scan",
    1247: "Setel Pay Scan",
  },
};

const DialogTitle = (props) => {
  const { children, onClose, ...other } = props;
  const classes = useStyles();

  return (
    <div className={classes.root} {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose && (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      )}
    </div>
  );
};

const OpenCash = ({ classes, value }) => {
  const cashTypeArray = [
    "10 cent",
    "20 cent",
    "50 cent",
    "1 ringgit",
    "5 ringgit",
    "10 ringgit",
  ];
  const cashInDetails = value?.CashInDetails || {};
  const cashOutDetails = value?.CashOutDetails || {};

  let totalCashReceived = cashInDetails.cashInTotal || 0;

  let totalCashReturned = cashOutDetails.cashOutTotal || 0;

  let balance = (totalCashReceived - totalCashReturned).toFixed(1);

  let totalCashTypeReceived = getCashType(
    cashInDetails.cashInTypeAndCount || {},
    "received",
  );

  let totalCashTypeReturned = getCashType(
    cashOutDetails.cashOutTypeAndCount || {},
    "returned",
  );
  return (
    <>
      <TableRow>
        <TableCell align="left" className={classes.keyCell}>
          <Typography
            variant="h5"
            style={{ fontSize: "1rem", color: "#263238", fontWeight: 510 }}
          >
            Payment Details
          </Typography>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell align="left" className={classes.keyCell}>
          Payment Type
        </TableCell>
        <TableCell align="left" className={classes.valueCell}>
          Cash
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell align="left" className={classes.keyCell}>
          Coin Type Received:
        </TableCell>
      </TableRow>
      {totalCashTypeReceived ? (
        totalCashTypeReceived.map((eachType, index) => (
          <TableRow key={index}>
            <TableCell align="left" colSpan={2} className={classes.valueCell}>
              {cashTypeArray[index]}
            </TableCell>
            <TableCell align="left" colSpan={2} className={classes.valueCell}>
              =
            </TableCell>
            <TableCell align="left" colSpan={2} className={classes.valueCell}>
              {eachType}
            </TableCell>
          </TableRow>
        ))
      ) : (
        <TableRow>
          <TableCell colSpan="3" align="right">
            N/A
          </TableCell>
        </TableRow>
      )}
      <TableRow>
        <TableCell
          align="left"
          colSpan={2}
          className={classes.valueCell}
        ></TableCell>
        <TableCell
          align="left"
          colSpan={2}
          style={{ fontWeight: 600 }}
          className={classes.valueCell}
        >
          Total:
        </TableCell>
        {totalCashTypeReceived ? (
          <TableCell
            align="left"
            colSpan={2}
            style={{ fontWeight: 600 }}
            className={classes.valueCell}
          >
            RM {totalCashReceived}
          </TableCell>
        ) : (
          <TableCell
            align="left"
            colSpan={2}
            style={{ fontWeight: 600 }}
            className={classes.valueCell}
          >
            N/A
          </TableCell>
        )}
      </TableRow>

      <TableRow>
        <TableCell align="left" className={classes.keyCell}>
          Coin Type Returned:
        </TableCell>
      </TableRow>
      {totalCashTypeReturned ? (
        totalCashTypeReturned.map((eachType, index) => (
          <TableRow key={index}>
            <TableCell align="left" colSpan={2} className={classes.valueCell}>
              {cashTypeArray[index]}
            </TableCell>
            <TableCell align="left" colSpan={2} className={classes.valueCell}>
              =
            </TableCell>
            <TableCell align="left" colSpan={2} className={classes.valueCell}>
              {eachType}
            </TableCell>
          </TableRow>
        ))
      ) : (
        <TableRow>
          <TableCell align="left" className={classes.valueCell}>
            N/A
          </TableCell>
        </TableRow>
      )}
      <TableRow>
        <TableCell
          align="left"
          colSpan={2}
          className={classes.valueCell}
        ></TableCell>
        <TableCell
          align="left"
          colSpan={2}
          style={{ fontWeight: 600 }}
          className={classes.valueCell}
        >
          Total:
        </TableCell>
        {totalCashTypeReturned ? (
          <TableCell
            align="left"
            colSpan={2}
            style={{ fontWeight: 600 }}
            className={classes.valueCell}
          >
            RM {totalCashReturned}
          </TableCell>
        ) : (
          <TableCell
            align="left"
            colSpan={2}
            style={{ fontWeight: 600 }}
            className={classes.valueCell}
          >
            N/A
          </TableCell>
        )}
      </TableRow>
      <TableRow>
        <TableCell
          align="left"
          colSpan={2}
          className={classes.valueCell}
        ></TableCell>
        <TableCell
          align="left"
          colSpan={2}
          style={{ fontWeight: 600 }}
          className={classes.valueCell}
        >
          Balance:
        </TableCell>
        {totalCashTypeReceived && totalCashTypeReturned ? (
          <TableCell
            align="left"
            colSpan={2}
            style={{ fontWeight: 600 }}
            className={classes.valueCell}
          >
            RM {balance}
          </TableCell>
        ) : (
          <TableCell
            align="left"
            colSpan={2}
            style={{ fontWeight: 600 }}
            className={classes.valueCell}
          >
            N/A
          </TableCell>
        )}
      </TableRow>
    </>
  );
};

const OpenCashless = ({ classes, value }) => {
  return (
    <>
      <TableRow>
        <TableCell align="left" className={classes.keyCell}>
          <Typography
            variant="h5"
            style={{ fontSize: "1rem", color: "#263238", fontWeight: 510 }}
          >
            Payment Details
          </Typography>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell align="left" colSpan={3} className={classes.keyCell}>
          Payment Type:
        </TableCell>
        <TableCell align="left" colSpan={3} className={classes.valueCell}>
          {checkNull(checkPaymentType(value))}
        </TableCell>
      </TableRow>
      {value?.Mode === "commercial" && (
        <>
          <TableRow>
            <TableCell align="left" colSpan={3} className={classes.keyCell}>
              Transaction ID:
            </TableCell>
            <TableCell align="left" colSpan={3} className={classes.valueCell}>
              {checkNull(getTransactionID(value))}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell align="left" colSpan={3} className={classes.keyCell}>
              Ref No:
            </TableCell>
            <TableCell align="left" colSpan={3} className={classes.valueCell}>
              {checkNull(getReferenceNo(value))}
            </TableCell>
          </TableRow>
        </>
      )}
      {value?.Mode === "mro" &&
        (value?.Payment?.paymentType === "mro" ||
        value?.Payment?.paymentType == null ? (
          <>
            <TableRow>
              <TableCell align="left" colSpan={3} className={classes.keyCell}>
                Staff Name:
              </TableCell>
              <TableCell align="left" colSpan={3} className={classes.valueCell}>
                {checkNull(value?.Employee?.fullName)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell align="left" colSpan={3} className={classes.keyCell}>
                Staff Card:
              </TableCell>
              <TableCell align="left" colSpan={3} className={classes.valueCell}>
                {checkNull(value?.ScanCode)}
              </TableCell>
            </TableRow>
          </>
        ) : (
          <>
            <TableRow>
              <TableCell align="left" colSpan={3} className={classes.keyCell}>
                Transaction ID:
              </TableCell>
              <TableCell align="left" colSpan={3} className={classes.valueCell}>
                {checkNull(getTransactionID(value))}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell align="left" colSpan={3} className={classes.keyCell}>
                Ref No:
              </TableCell>
              <TableCell align="left" colSpan={3} className={classes.valueCell}>
                {checkNull(getReferenceNo(value))}
              </TableCell>
            </TableRow>
          </>
        ))}
    </>
  );
};

const OpenFailDetails = ({ value, classes }) => {
  const { category, message } = getErrorCategoryMessage(value);

  return (
    <>
      <TableRow>
        <TableCell align="left" className={classes.keyCell}>
          <Typography
            variant="h5"
            style={{ fontSize: "1rem", color: "#263238", fontWeight: 510 }}
          >
            Error Detail
          </Typography>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell align="left" colSpan={3} className={classes.keyCell}>
          Category:
        </TableCell>
        <TableCell align="left" colSpan={3} className={classes.valueCell}>
          {checkNull(category)}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell align="left" colSpan={3} className={classes.keyCell}>
          Message:
        </TableCell>
        <TableCell align="left" colSpan={3} className={classes.valueCell}>
          {checkNull(message)}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell align="left" colSpan={3} className={classes.keyCell}>
          Description:
        </TableCell>
        <TableCell align="left" colSpan={3} className={classes.valueCell}>
          {checkNull(getErrorDetails(value))}
        </TableCell>
      </TableRow>
    </>
  );
};

const checkStatus = (classes, v) => {
  if (
    !v ||
    (typeof v.Status === "undefined" && typeof v.status === "undefined")
  ) {
    console.error("Invalid input to checkStatus function:", v);
    return (
      <TableCell align="left" colSpan={1} className={classes.valueCell}>
        N/A
      </TableCell>
    );
  }

  const status = v.Status ?? v.status;

  switch (status) {
    case -4:
      return (
        <TableCell
          align="left"
          style={{ color: "#FF6562" }}
          colSpan={3}
          className={classes.valueCell}
        >
          DispensingEr
        </TableCell>
      );
    case -3:
      return (
        <TableCell
          align="left"
          style={{ color: "#FF6562" }}
          colSpan={3}
          className={classes.valueCell}
        >
          Void
        </TableCell>
      );
    case -2:
      return (
        <TableCell align="left" style={{ color: "#FF6562" }} colSpan={3}>
          Dropped
        </TableCell>
      );
    case -1:
      return (
        <TableCell align="left" style={{ color: "#C0C0C0" }} colSpan={3}>
          Canceled
        </TableCell>
      );
    case 0:
      return (
        <TableCell align="left" style={{ color: "#FF6562" }} colSpan={3}>
          Failed
        </TableCell>
      );
    case 1:
      return (
        <TableCell
          align="left"
          style={{ color: "#00C07F" }}
          colSpan={3}
          className={classes.valueCell}
        >
          Successful
        </TableCell>
      );
  }
};

const checkPaymentType = (record) => {
  try {
    console.log(record, "mana record saya6666111");
    if (record?.Payment?.gatewayID) {
      return TRANSACTION.GATEWAY?.[record.Payment.gatewayID] || "N/A";
    }

    if (record?.Payment?.cash) {
      return "Cash Coin";
    }

    if (record?.Payment?.terminal) {
      return "Paywave";
    }

    if (record?.Payment?.ghlPaymentDetail?.paymentResponse?.msg?.productCode) {
      return record.Payment.ghlPaymentDetail.paymentResponse.msg.productCode;
    }

    if (record?.Payment?.virclePaymentDetail) {
      return "Vircle";
    }

    if (record?.Payment?.onePay) {
      return "1Pay";
    }

    if (record?.Mode === "mro" || record?.Payment?.paymentType === "mro") {
      return record?.Payment?.paymentType || "mro";
    }

    return "";
  } catch (error) {
    return "";
  }
};

const getTransactionID = (record) => {
  if (record?.Payment?.transactionId) {
    return record.Payment.transactionId;
  }

  if (record?.Payment?.ghlPaymentDetail?.paymentResponse?.msg?.txnRef) {
    return record.Payment.ghlPaymentDetail.paymentResponse.msg.txnRef;
  }

  return "";
};

const getReferenceNo = (record) => {
  if (record?.Payment?.refNo) {
    return record.Payment.refNo;
  }

  if (record?.Payment?.ghlPaymentDetail?.paymentResponse?.msg?.retTxnRef) {
    return record.Payment.ghlPaymentDetail.paymentResponse.msg.retTxnRef;
  }

  return "";
};

const getErrorCategoryMessage = (record) => {
  const errorDescription = record?.Error?.description || "";

  if (!errorDescription) return { category: "", message: "" };

  const desc = errorDescription.toLowerCase();

  const matchDetail = ErrorDetailList.find((err) =>
    err.key.some((k) => desc.includes(k.toLowerCase())),
  );

  if (!matchDetail) return { category: "", message: "" };

  const isDriverBoardKey =
    matchDetail.key.includes("driver board went wrong") ||
    matchDetail.key.includes("no item detect dropped");

  if (isDriverBoardKey) {
    const driverBoardResponse = record?.ResponseStatus || {};

    const { globalDroppedSensor, itemDroppedSensor, motorTurned, itemDropped } =
      driverBoardResponse;

    if (!globalDroppedSensor && !itemDroppedSensor) {
      return {
        category: matchDetail.category,
        message: `${matchDetail.message} — sensor off`,
      };
    }

    if (!motorTurned) {
      return {
        category: matchDetail.category,
        message: `${matchDetail.message} — motor didn’t spin`,
      };
    }

    if (!itemDropped) {
      return {
        category: matchDetail.category,
        message: `${matchDetail.message} — sensor didn’t detect drop`,
      };
    }
  }
  return {
    category: matchDetail.category,
    message: matchDetail.message,
  };
};

const getErrorDetails = (value) => {
  let errorDescription = value?.Error?.description || "";
  errorDescription = `${errorDescription
    .replaceAll("com.vechnology.ve.models.payment.", "*")
    .replaceAll("com.vechnology.ve.models.serial.driverBoard.", "*")
    .replaceAll("com.vechnology.ve", "*")
    .substring(0, 100)}...`;
  return errorDescription;
};

const getTime = (dateTime, type) => {
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "Jun",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const date = new Date(dateTime);

  if (type === "date") {
    return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
  }
  if (type === "time") {
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const seconds = date.getSeconds().toString().padStart(2, "0");
    return `${hours}:${minutes}:${seconds}`;
  }

  return "N/A";
};

const generateCashTypeArray = (cashTypeObject, arrayType) => {
  console.log(cashTypeObject, "mana cashTypeObject saya6666111");
  console.log(arrayType, "mana arrayType saya6666111");
  const receivedArray = ["0.1", "0.2", "0.5", "1.0", "5.0", "10.0"];
  const returnedArray = ["0.1", "0.2", "0.5"];
  const selectedArray =
    arrayType === "received" ? receivedArray : returnedArray;

  return selectedArray.map((type) => {
    const value = cashTypeObject?.[type] || 0;
    return value.toString();
  });
};

const getCashType = (cashTypeObject, transType) => {
  console.log(cashTypeObject, "mana cashTypeObject saya6666111");
  console.log(transType, "mana transType saya6666111");
  if (!cashTypeObject) return [];
  switch (transType) {
    case "received":
      return generateCashTypeArray(cashTypeObject, "received");
    case "returned":
      return generateCashTypeArray(cashTypeObject, "returned");
    default:
      return [];
  }
};

const getProductSlot = (record) => {
  if (record === undefined || record === null || isNaN(record)) return "N/A";
  const rowArray = ["A", "B", "C", "D", "E", "F"];
  if (record === null) return "N/A";
  let row = Math.floor(record / 12);
  let column = record % 12;
  return `${rowArray[row]}${column}`;
};

const checkNull = (value) => (value != null ? value : "N/A");

export default function CustomizedDialogs({ record, onClose, open }) {
  const value = record;
  const classes = useStyles();

  return (
    <>
      <Dialog
        onClose={onClose}
        aria-labelledby="customized-dialog-title"
        open={open}
        className={classes.customDialog}
      >
        <DialogTitle id="customized-dialog-title" onClose={onClose}>
          Id: {value?.TransactionId ?? "N/A"}
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Table className={classes.table}>
            <TableBody>
              <TableRow>
                <TableCell align="left" className={classes.keyCell} colSpan={1}>
                  <Typography
                    variant="h5"
                    style={{
                      fontSize: "1rem",
                      color: "#263238",
                      fontWeight: 510,
                    }}
                  >
                    Transaction Details
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align="left" colSpan={3} className={classes.keyCell}>
                  Machine UUID:{" "}
                </TableCell>
                <TableCell
                  align="left"
                  colSpan={3}
                  className={classes.valueCell}
                >
                  {checkNull(value?.Machine?.machineUUID)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align="left" colSpan={3} className={classes.keyCell}>
                  Date:{" "}
                </TableCell>
                <TableCell
                  align="left"
                  colSpan={3}
                  className={classes.valueCell}
                >
                  {checkNull(getTime(value?.Date, "date"))}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align="left" colSpan={3} className={classes.keyCell}>
                  Time:{" "}
                </TableCell>
                <TableCell
                  align="left"
                  colSpan={3}
                  className={classes.valueCell}
                >
                  {checkNull(getTime(value?.Date, "time"))}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align="left" colSpan={3} className={classes.keyCell}>
                  Product Name:{" "}
                </TableCell>
                <TableCell
                  align="left"
                  colSpan={3}
                  className={classes.valueCell}
                >
                  {checkNull(value?.Product?.name)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align="left" colSpan={3} className={classes.keyCell}>
                  Product Slot:{" "}
                </TableCell>
                <TableCell
                  align="left"
                  colSpan={3}
                  className={classes.valueCell}
                >
                  {checkNull(
                    getProductSlot(
                      value?.Product?.slotId ?? value?.Product?.slot,
                    ),
                  )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align="left" colSpan={3} className={classes.keyCell}>
                  Product Price:{" "}
                </TableCell>
                <TableCell
                  align="left"
                  colSpan={3}
                  className={classes.valueCell}
                >
                  {(() => {
                    const mode = value?.mode;
                    const amount = value?.Amount;
                    const paymentType = value?.Payment?.paymentType;

                    if (
                      mode === "mro" &&
                      (paymentType == null || paymentType === "mro")
                    ) {
                      return `${amount} Credit${amount !== 1 ? "s" : ""}`;
                    } else {
                      return `RM ${amount}`;
                    }
                  })()}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align="left" colSpan={3} className={classes.keyCell}>
                  Status:{" "}
                </TableCell>
                {checkStatus(classes, value)}
              </TableRow>
              {value?.Status === 1 ? (
                <></>
              ) : (
                <OpenFailDetails classes={classes} value={value} />
              )}
              {value?.Payment?.cash ? (
                <OpenCash value={value} classes={classes} />
              ) : (
                <OpenCashless value={value} classes={classes} />
              )}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </>
  );
}
